import { useState } from 'react';
import { Button, Switch } from '@accure/ui';
import { SquarePen } from 'lucide-react';
import { tv } from 'tailwind-variants';

import type { AlertNotificationRule } from '@/api/fe-api/domain/entities/alert-notifications';
import { UpdateRule } from '@/api/fe-api/view/mutations/alert-notifications/update-rule';
import { Card } from '@/components/ui/card';
import { Tooltip } from '@/components/ui/tooltip';
import { useMe } from '@/hooks/use-me';
import { toastResult } from '@/utils/toast';

import { BadgeMinAlertStatus } from '../badge-min-alert-status';
import { TooltipAdditionalRecipients } from '../tooltip-additional-recipients';
import { iconsFrequency, labelsFrequency } from '../types';

import { AssetList } from './asset-list';
import { BadgeListCollapsed } from './badge-list-collapsed';

export type NotificationRuleProps = {
  data: AlertNotificationRule;
  onEditClick: (data: AlertNotificationRule) => void;
};

export function NotificationRule({ data, onEditClick }: NotificationRuleProps) {
  const [isEnabled, setIsEnabled] = useState(Boolean(data.isEnabled));
  const style = notificationRuleStyle({ enabled: isEnabled });
  const updateRule = UpdateRule.useMutation();

  const { config } = useMe();

  const FrequencyIcon = iconsFrequency[data.frequency];

  function onToggleEnabled(isChecked: boolean) {
    setIsEnabled(isChecked);
    if (isChecked) {
      toastResult(
        updateRule.mutateAsync({
          id: data.description ?? data.id,
          isEnabled: isChecked,
        }),
        {
          success: 'Notification rule enabled',
          error: 'Failed to update the status',
          exception: 'Failed to update the status',
        },
      );
    }
  }

  return (
    <Card className="space-y-4 p-8">
      <div className="flex flex-col gap-6 border-b border-neutral-200 pb-2 md:flex-row md:items-center md:justify-between">
        <div
          className={style.content({
            className:
              'flex items-center justify-between gap-4 md:justify-start',
          })}
        >
          <h3 className="text-lg font-semibold">{data.description}</h3>
          <Tooltip content="Edit notification rule">
            <Button
              variant="ghost"
              onClick={() => onEditClick(data)}
              className="text-neutral-500"
              size="icon"
            >
              <SquarePen />
            </Button>
          </Tooltip>
        </div>
        <div className="flex justify-between gap-4 md:justify-start">
          <label className="cursor-pointer" htmlFor={`${data.id}-isEnabled`}>
            Enabled
          </label>
          <Switch
            id={`${data.id}-isEnabled`}
            value={isEnabled}
            onChange={onToggleEnabled}
          />
        </div>
      </div>
      <div
        className={style.content({
          className: 'grid grid-cols-1 gap-6 md:grid-cols-2',
        })}
      >
        <div>
          <h3 className={style.label()}>Frequency of notifications</h3>
          <div className="flex items-center gap-2">
            <FrequencyIcon className="size-4 text-neutral-400" />
            {labelsFrequency[data.frequency]}
          </div>
        </div>
        <div>
          <h3 className={style.label()}>Alert Severity</h3>
          <div className="flex items-center gap-2">
            <BadgeMinAlertStatus minAlertStatus={data.minAlertStatus} />
          </div>
        </div>
        {config.type === 'mobility' ? null : (
          <div>
            <h3 className={style.label()}>Assets</h3>
            <AssetList data={data} />
          </div>
        )}
        {data.externalEmails.length > 0 ? (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h3 className={style.label({ className: 'mb-0' })}>Notify</h3>
              <TooltipAdditionalRecipients />
            </div>
            <BadgeListCollapsed
              items={data.externalEmails}
              defaultVisible={5}
            />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

const notificationRuleStyle = tv({
  slots: {
    content: '',
    label: 'mb-2 text-xs text-neutral-500',
  },
  variants: {
    enabled: {
      false: {
        content: 'opacity-50',
      },
    },
  },
  defaultVariants: {
    enabled: true,
  },
});
